import { ChangeEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormikErrors, useFormik } from 'formik';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import UserAPI from '../../API/userAPI';
import { setAuthInfo } from './userSlice';
import { loadCart, selectCartCoupon, selectCartItems, setCartCoupon, setCartItem } from '../cart/cartSlice';
import { loadAffiliate, loadLogoUrl, resetReferrals, selectAffiliate, setEmbedded } from '../referrals/referralsSlice';
import { clearDietaryRestrictions, selectProducts } from '../products/productsSlice';
import { getCouponByCode } from '../../API/coupons';
import CouponObj from '../coupons/couponObj';
import CartObj from '../cart/cartObj';
import { selectCustomer } from '../customer/customerSlice';
import CustomerObj from '../customer/customerObj';
import AffiliateObj from '../referrals/AffiliateObj';
import { selectVisitorID, setVisitorID } from '../mobile/mobileSlice';

interface Response {
  token: string;
  user_email: string;
  user_nicename: string;
  user_display_name: string;
  data: { status: number };
}

interface FormValues {
  loginEmail: string;
  loginPassword: string;
  loginRememberMe: string;
}

export default function LoginForm() {
  const dispatch = useDispatch();
  const [errorMsg, setErrorMsg] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [params] = useSearchParams();
  const successMsg = params.get('successMsg');
  const cartItems = useSelector(selectCartItems);
  const cartCoupon = useSelector(selectCartCoupon);
  const cart = new CartObj(useSelector(selectCartItems));
  const products = useSelector(selectProducts);
  const customer = new CustomerObj(useSelector(selectCustomer));
  const affiliateData = useSelector(selectAffiliate);
  const affiliateVisitorID = useSelector(selectVisitorID);
  const affiliateSavedData = new AffiliateObj(affiliateData);
  const affiliateMeta = affiliateSavedData?.data;

  const handleRememberMeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      formik.setFieldValue('loginRememberMe', '1');
    } else {
      formik.setFieldValue('loginRememberMe', '0');
    }
  }

  const getCouponCodeFromQuery = (token: any, email: any, couponCode: any) => {
    getCouponByCode(
      couponCode
    ).then((coupons: Array<Record<string, any>>) => {
      if (!coupons.length) {
        setErrorMsg('Coupon "' + couponCode + '" is invalid.');
        return;
      }
      let coupon = new CouponObj(coupons[0]);
      let validationResult = coupon.validate(email, customer, cart, products);
      if (validationResult.error) {
        setErrorMsg(validationResult.message);
        return;
      }
      dispatch(setCartCoupon({ token: token, coupon: coupons[0] }));
    }).catch((e) => {
      console.error(e);
      setErrorMsg('An unexpected error has occurred. Please try again later.');
    });
  }
  
  const validate = (values: FormValues) => {
    let errors: FormikErrors<FormValues> = {};
    if (!values.loginEmail) {
      errors.loginEmail = "Required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.loginEmail)) {
      errors.loginEmail = "Invalid email address";
    }
    if (!values.loginPassword) {
      errors.loginPassword = "Required";
    }
    return errors;
  }

  const formik = useFormik({
    initialValues: {
      loginEmail: '',
      loginPassword: '',
      loginRememberMe: '0'
    },
    validate,
    onSubmit: values => {
      setErrorMsg('');
      setLoggingIn(true);
      UserAPI.authenticateUser(
        values.loginEmail, 
        values.loginPassword, 
        values.loginRememberMe === '1'
      ).then(async (response: Response) => {
        if ('data' in response && response.data.status === 403) {
          setErrorMsg("Invalid email/password combination.");
          setLoggingIn(false);
        } else if ('code' in response) {
          setErrorMsg("An error has occurred. Please try again later.");
          setLoggingIn(false);
        } else {
          if (values.loginRememberMe === '1') {
            try {
              localStorage.setItem('token', response.token);
            } catch (e) {
              console.error("Error adding token to local storage:", e);
            }
          }
          const setLoginInfo = {
            token: response.token,
            email: response.user_email, 
            nicename: response.user_nicename,
            display_name: response.user_display_name,
            isRememberMe: values.loginRememberMe === '1',
            dateStored: new Date()
          }
          localStorage.setItem('setAuthInfo', JSON.stringify(setLoginInfo));
          dispatch(clearDietaryRestrictions());
          dispatch(setAuthInfo({ 
            token: response.token,
            email: response.user_email, 
            nicename: response.user_nicename,
            display_name: response.user_display_name
          }));
          dispatch(resetReferrals())
          if (Object.entries(cartItems).length > 0) {
            // eslint-disable-next-line array-callback-return
            Object.entries(cartItems).map((tempItems: any, i) => {
              dispatch(
                setCartItem({
                  token: response.token,
                  cart_item: {
                    product_id: tempItems[1]?.product_id,
                    product_qty: tempItems[1]?.product_qty,
                    product_price: tempItems[1]?.product_price,
                  },
                })
              );
            });
          }
          await dispatch(loadCart(response.token));
          getCouponCodeFromQuery(response.token,response.user_email, cartCoupon.code);
        }
      }).catch((e) => {
        setErrorMsg(e.message);
      }).finally(() => {

          if((affiliateMeta?.meta?.custom_slug?.length !== undefined || affiliateMeta?.meta?.custom_slug?.length > 0) && 
             (affiliateMeta.affiliate_id !== undefined || affiliateMeta.affiliate_id !== null)){

            dispatch(loadLogoUrl(affiliateSavedData.getCustomSlug()));
            dispatch(setEmbedded(true));
            if(affiliateVisitorID){
              dispatch(setVisitorID(`${affiliateVisitorID}`))
            }
            if(affiliateMeta.affiliate_id) {
              dispatch(loadAffiliate(affiliateMeta.affiliate_id));
            }
          }
          
      });
    }
  });

  return (
    <Form className='login-form' onSubmit={formik.handleSubmit}>
      {successMsg &&
        <Alert className='mb-4' variant='success'>{successMsg}</Alert> 
      }
      {errorMsg && <p className='text-danger'>{errorMsg}</p>}
      <Form.Group className="mb-3 form-group required">
        <Form.Label>Email address</Form.Label>
        <Form.Control 
          id="loginEmail"
          type="email"
          size="lg"
          isValid={Boolean(formik.values.loginEmail) &&
            !Boolean(formik.errors.loginEmail)}
          isInvalid={Boolean(formik.errors.loginEmail)}
          value={formik.values.loginEmail}
          onChange={formik.handleChange}
        />
        {formik.errors.loginEmail &&
          <Form.Control.Feedback type="invalid">
            {formik.errors.loginEmail}
          </Form.Control.Feedback>}
      </Form.Group>
      <Form.Group className="mb-3 form-group required">
        <Form.Label>Password</Form.Label>
        <Form.Control 
          id="loginPassword"
          type="password" 
          size="lg"
          isValid={Boolean(formik.values.loginPassword) && 
            !Boolean(formik.errors.loginPassword)}
          isInvalid={Boolean(formik.errors.loginPassword)}
          value={formik.values.loginPassword}
          onChange={formik.handleChange}
        />
        {formik.errors.loginPassword &&
          <Form.Control.Feedback type="invalid">
            {formik.errors.loginPassword}
          </Form.Control.Feedback>}
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label
         className='d-inline-flex align-items-center'
        >
          <Form.Check
          className='d-inline me-2'
          id="loginRememberMe"
          value={formik.values.loginRememberMe}
          onChange={handleRememberMeChange}
        />
        Remember me</Form.Label>
      </Form.Group>
      {loggingIn ? 
        <Button className='bg-black' size="lg" disabled>
          <Spinner animation="border" as="span" size="sm" />
          &nbsp;&nbsp;Logging in ...
        </Button> :
        <Button className='bg-black' size="lg" type="submit">
          Login
        </Button>
      }
      <Link 
        to='/my-account/lost-password'
        className='text-black d-block my-4'
      >
        Lost your password?
      </Link>
    </Form>
 );
}